<template>
    <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" :aria-labelledby="modalId + 'Label'"
        aria-hidden="true" data-testid="modal-proposition" data-backdrop="static" data-keyboard="false">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" :id="modalId + 'Label'">
                        <i class="bi bi-pencil-square mr-2" aria-hidden="true"></i>
                        {{ messages.modalTitle }}
                    </h5>
                    <button type="button" class="close" data-dismiss="modal" data-testid="modal-close-btn"
                        :aria-label="messages.modalCloseLabel" @click="$event.currentTarget.blur()">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <form @submit.prevent="soumettreProposition" novalidate>

                        <!-- Texte saisi -->
                        <div class="form-group">
                            <label for="modal-source-text" class="font-weight-bold">{{ messages.modalSourceLabel
                                }}</label>
                            <p id="modal-source-text"
                                class="output-text-display form-control-plaintext border rounded px-3 py-2 bg-light mb-0"
                                aria-readonly="true"
                                style="height:auto;max-height:15rem;overflow-y:auto;white-space:pre-wrap;word-break:break-word;line-height:1.6;">
                                {{ inputText || '' }}
                            </p>
                        </div>

                        <!-- Terme traduit avec score -->
                        <div class="form-group">
                            <label for="modal-translated-text" class="font-weight-bold" style="font-size: 14px;">{{
                                messages.modalTranslatedLabel }}</label>
                            <div id="modal-translated-text"
                                class="output-text-display form-control-plaintext border rounded px-3 py-2 bg-light mb-0 d-flex justify-content-between align-items-start"
                                role="textbox" aria-readonly="true"
                                style="height:auto;max-height:15rem;overflow-y:auto;">
                                <span v-if="translatedText" v-html="highlightedHtml"
                                    style="white-space:pre-wrap;word-break:break-word;line-height:1.6;flex:1;"></span>
                                <span v-else class="text-muted">—</span>
                                <span v-if="translationScore !== null"
                                    :class="['badge', 'badge-pill', 'ms-2', 'shrink-0', badgeClass(translationScore)]">
                                    {{ formatScore(translationScore) }}
                                </span>
                            </div>
                        </div>

                        <!-- Nouvelle proposition -->
                        <div class="form-group">
                            <label for="proposition-traduction" class="font-weight-bold">
                                {{ messages.modalPropositionLabel }}
                                <span class="text-danger" aria-hidden="true">*</span>
                            </label>
                            <textarea id="proposition-traduction" type="text" class="form-control"
                                data-testid="input-proposition-terme" rows="3"
                                :class="{ 'is-invalid': propositionSoumise && !proposition.terme }"
                                v-model="proposition.terme" :placeholder="messages.modalPropositionPlaceholder"
                                aria-required="true"></textarea>
                            <div class="invalid-feedback">{{ messages.modalFieldRequired }}</div>
                        </div>

                        <!-- Commentaire obligatoire -->
                        <div class="form-group">
                            <label for="proposition-commentaire" class="font-weight-bold">
                                {{ messages.modalCommentLabel }}
                                <span class="text-danger" aria-hidden="true">*</span>
                            </label>
                            <textarea id="proposition-commentaire" class="form-control" rows="3"
                                data-testid="input-proposition-commentaire"
                                :class="{ 'is-invalid': propositionSoumise && !proposition.commentaire }"
                                v-model="proposition.commentaire" :placeholder="messages.modalCommentPlaceholder"
                                aria-required="true"></textarea>
                            <div class="invalid-feedback">{{ messages.modalFieldRequired }}</div>
                        </div>

                    </form>
                </div>
                <div class="modal-footer">
                    <button type="button" data-dismiss="modal" class="btn btn--ghost btn--default"
                        data-testid="modal-cancel-btn" @click="$event.currentTarget.blur()">{{ messages.modalCancelLabel
                        }}</button>
                    <button id="btn-traduire" type="button" class="btn btn--plain btn--primary"
                        data-testid="modal-submit-btn" @click="soumettreProposition" aria-label="Lancer la traduction">
                        {{ messages.modalSubmitLabel }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import translatorConfig from '../config/translator.config.js';
import { badgeClass, buildHighlightedHtml, formatScore } from '../utils/translatorUtils.js';
import Store from '../store-translator.js';

export default {
    name: 'TranslatorProposalModal',
    props: {
        modalId: {
            type: String,
            default: 'modalProposerTraduction'
        },
        inputText: {
            type: String,
            default: ''
        },
        translatedText: {
            type: String,
            default: ''
        },
        highlightedTerms: {
            type: Array,
            default: () => []
        },
        translationScore: {
            type: Number,
            default: null
        },
        modelId: {
            type: String,
            default: null
        },
        direction: {
            type: String,
            default: null
        },
        ordre: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            messages: translatorConfig.output.messages,
            propositionSoumise: false,
            proposition: {
                terme: '',
                commentaire: ''
            }
        }
    },
    computed: {
        highlightedHtml() {
            return buildHighlightedHtml(this.translatedText, this.highlightedTerms)
        }
    },
    methods: {
        badgeClass,
        formatScore,
        resetPropositionForm() {
            this.propositionSoumise = false
            this.proposition = { terme: '', commentaire: '' }
        },
        closeModal() {
            const el = document.getElementById(this.modalId)
            if (!el) return

            const jq = window.jQuery || window.$
            if (jq && jq.fn && jq.fn.modal) {
                jq(el).modal('hide')
                return
            }

            if (window.bootstrap && window.bootstrap.Modal) {
                window.bootstrap.Modal.getInstance(el)?.hide()
            }
        },
        async soumettreProposition() {
            this.propositionSoumise = true
            if (!this.proposition.terme || !this.proposition.commentaire) return

            await Store.soumettreVote({
                positive: false,
                terme: this.proposition.terme,
                commentaire: this.proposition.commentaire,
                inputText: this.inputText,
                translatedText: this.translatedText,
                modelId: this.modelId,
                direction: this.direction,
                ordre: this.ordre
            })

            this.resetPropositionForm()
            this.$emit('proposal-submitted')
            this.closeModal()
        }
    }
}
</script>

<style scoped>
.highlight-term {
    background-color: #fff3cd;
    color: #664d03;
    border-radius: 3px;
    padding: 1px 3px;
    font-weight: 600;
}
</style>
